import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { fmtIDR, fmtWaktuEntry, fmtTanggal, todayLocal, formatRibuan, parseRibuan } from "../lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Clock, Loader2, Save } from "lucide-react";

export default function EntrySaldo() {
  const [tanggal, setTanggal] = useState(todayLocal());
  const [saldo, setSaldo] = useState("");
  const [keterangan, setKeterangan] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [now, setNow] = useState(new Date());
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const loadRecent = () => {
    api.get("/saldo").then((r) => setRecent(r.data.slice(-5).reverse())).catch(() => {});
  };
  useEffect(loadRecent, []);

  const waktuSekarang = now.toLocaleString("id-ID", {
    timeZone: "Asia/Jakarta",
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const handleSaldoChange = (e) => {
    const digits = e.target.value.replace(/[^\d]/g, "").slice(0, 15);
    setSaldo(digits ? formatRibuan(digits) : "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nilai = parseRibuan(saldo);
    if (!saldo || nilai <= 0) {
      toast.error("Total saldo harus diisi lebih dari 0");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/saldo", {
        tanggal,
        total_saldo: nilai,
        keterangan,
      });
      toast.success(`Saldo ${fmtTanggal(tanggal)} tersimpan. Waktu entry: ${fmtWaktuEntry(data.created_at)}`);
      setSaldo("");
      setKeterangan("");
      loadRecent();
    } catch (err) {
      toast.error(apiError(err, "Gagal menyimpan data"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="entry-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Entry Posisi Saldo</h1>
        <p className="text-sm text-slate-500 mt-1">
          Input total saldo harian. Waktu entry (tanggal &amp; jam) tercatat otomatis oleh sistem.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Form Entry Saldo</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="entry-form">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="tanggal">Tanggal Saldo</Label>
                  <Input
                    id="tanggal"
                    type="date"
                    data-testid="entry-date-input"
                    value={tanggal}
                    onChange={(e) => setTanggal(e.target.value)}
                    required
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="saldo">Total Saldo (Rp)</Label>
                  <Input
                    id="saldo"
                    inputMode="numeric"
                    data-testid="entry-account-input-total"
                    value={saldo}
                    onChange={handleSaldoChange}
                    placeholder="contoh: 150.000.000"
                    required
                    className="h-11 font-num"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="keterangan">Keterangan (opsional)</Label>
                <Textarea
                  id="keterangan"
                  data-testid="entry-keterangan-input"
                  value={keterangan}
                  onChange={(e) => setKeterangan(e.target.value)}
                  placeholder="Catatan tambahan, misal: setelah transfer masuk dari klien X"
                  rows={3}
                />
              </div>
              <Button
                type="submit"
                data-testid="entry-submit-button"
                disabled={submitting}
                className="h-11 bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Menyimpan...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" /> Simpan Saldo
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="bg-slate-900 text-white border-slate-800" data-testid="entry-clock-card">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-400">
                <Clock className="h-4 w-4 text-emerald-400" /> Waktu Entry Otomatis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-num text-lg font-semibold text-emerald-400" data-testid="entry-live-clock">
                {waktuSekarang} WIB
              </p>
              <p className="mt-2 text-xs text-slate-400">
                Saat tombol Simpan ditekan, tanggal &amp; jam ini dicatat sistem sebagai waktu entry.
              </p>
            </CardContent>
          </Card>

          <Card data-testid="entry-recent-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                5 Entry Terakhir
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {recent.length === 0 && (
                <p className="text-sm text-slate-500" data-testid="entry-recent-empty">Belum ada data.</p>
              )}
              {recent.map((r) => (
                <div key={r.id} className="flex items-start justify-between gap-2 border-b border-slate-100 pb-2 last:border-0" data-testid={`entry-recent-item-${r.tanggal}`}>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">
                      {r.hari}, {fmtTanggal(r.tanggal)}
                    </p>
                    <p className="font-num text-[11px] text-slate-500">{fmtWaktuEntry(r.created_at)}</p>
                  </div>
                  <p className="font-num text-sm font-bold text-slate-900">{fmtIDR(r.total_saldo)}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
